import React, { Component } from 'react';
import PropTypes from 'prop-types';
import Button, { ButtonGroup } from '@pattern-library/buttons';
import Dropdown from '@pattern-library/dropdown';
import { registerFilter, updateFilter, resetFilter, resetAllFilters } from '../stateReducer/actions';
import { createRowByQueryOnKeysFilter } from '../utils/data-management';
import unwrapRef from '../utils/unwrapRef';
import { DataGridContext, FilterContext } from './context';
import DataGridFilter from './DataGridFilter';
import DataGridFilterTags from './DataGridFilterTags';
const propTypes = {
  /**
   * Configurations for additional filters that will be hidden behind the toggle button
   */
  additionalFilters: PropTypes.arrayOf(PropTypes.shape({
    /**
     * Additional information to display below the filter control.
     */
    additionalInfo: PropTypes.node,
    /**
     * Only used for checkbox filters, this object should contain the
     * labels for the checkboxes. The key should be the value of the
     * checkbox and the value should be an object with a `label` key.
     * The object can also contain a `additionalInfo` key with a node
     * to render below the checkbox.
     *
     * ```
     *  [
     *      {
     *          default: {
     *              label: 'Default'
     *          },
     *      }
     *  ]
     * ```
     */
    checkboxLabels: PropTypes.objectOf(PropTypes.object),
    /**
     * The row data key to filter on.
     */
    dataKey: PropTypes.string.isRequired,
    /**
     * A function to filter the rows. This function accepts the row data
     * and should return a boolean, where `true` means the row should be
     * included in the filtered results.
     */
    filterFunction: PropTypes.func,
    /**
     * The label for the filter.
     */
    label: PropTypes.string,
    /**
     * The order of the filter in the list of filters.
     * Filters are sorted in ascending order.
     */
    order: PropTypes.number,
    /**
     * Label for the toggle filter.
     */
    toggleLabel: PropTypes.string,
    /**
     * The type of filter to render.
     */
    type: PropTypes.oneOf(['select', 'checkbox', 'toggle']).isRequired
  })),
  /**
   * HTML classes to be applied to the container element
   */
  className: PropTypes.string,
  /**
   * The element to be displayed in the "search" area of the filters
   */
  search: PropTypes.node,
  /**
   * The element to be displayed in the "search form" area of the filters
   */
  searchForm: PropTypes.node
};
export const SEARCH_KEY = '__asc-filter--search__';
const SEARCH_FORM_KEY = '__asc-filter--search-form__';
export default class DataGridFilters extends Component {
  constructor(props) {
    super(props);
    this.registerSearchFilter = keys => {
      this.registerInputFilter(SEARCH_KEY, keys);
    };
    this.registerSearchFormFilter = keys => {
      this.registerInputFilter(SEARCH_FORM_KEY, keys);
    };
    this.registerAdditionalFilter = (filter, value) => {
      const {
        dispatch
      } = this.context;
      const includeRow = filter.filterFunction ? filter.filterFunction : (row, values) => values.indexOf(row[filter.dataKey]) > -1;
      dispatch(registerFilter({
        key: filter.dataKey,
        value,
        defaultValue: [],
        includeRow,
        isActive: values => values.length > 0
      }));
    };
    this.handleUpdateSearch = query => {
      const {
        dispatch
      } = this.context;
      dispatch(updateFilter(SEARCH_KEY, query));
      dispatch(resetFilter(SEARCH_FORM_KEY));
    };
    this.handleUpdateSearchForm = query => {
      const {
        dispatch
      } = this.context;
      dispatch(updateFilter(SEARCH_FORM_KEY, query));
      dispatch(resetAllFilters({
        exceptKey: SEARCH_FORM_KEY
      }));
    };
    this.handleUpdateFilter = (filter, value) => {
      const {
        dispatch
      } = this.context;
      dispatch(updateFilter(filter.dataKey, value));
    };
    this.handleToggleFilters = () => {
      this.state.filtersOpen ? this.closeFilters() : this.openFilters();
    };
    this.handleClearFilter = (key, value) => {
      const {
        filter,
        dispatch
      } = this.context;
      const filterToClear = filter[key];
      if (!filterToClear) {
        return;
      }
      if (Array.isArray(filterToClear.value)) {
        dispatch(updateFilter(key, filterToClear.value.filter(v => v !== value)));
      } else {
        dispatch(resetFilter(key));
      }
    };
    this.handleClearAllFilters = () => {
      const {
        dispatch
      } = this.context;
      dispatch(resetAllFilters());
    };
    this.state = {
      closeFilters: false
    };
  }
  componentWillUnmount() {
    if (this.filterTimer) {
      window.clearTimeout(this.filterTimer);
    }
  }
  registerInputFilter(filterKey, keys) {
    const {
      dispatch
    } = this.context;
    dispatch(registerFilter({
      key: filterKey,
      defaultValue: '',
      includeRow: createRowByQueryOnKeysFilter(keys),
      isActive: value => value
    }));
  }
  closeFilters() {
    this.setState({
      closeFilters: true
    }, () => {
      window.setTimeout(() => {
        this.setState({
          closeFilters: false
        });
      }, 100);
    });
  }
  buildFilterOptions(filter) {
    if (filter.options) {
      return filter.options;
    }
    const {
      allRows
    } = this.context;
    return allRows.map(row => row[filter.dataKey]).sort().filter((rowValue, index, values) => {
      // de-duplicate options
      return rowValue && rowValue !== values[index - 1];
    });
  }
  render() {
    const {
      columns,
      filter = {}
    } = this.context;
    const {
      className,
      search,
      searchForm,
      additionalFilters
    } = this.props;
    const {
      closeFilters
    } = this.state;
    const searchQuery = filter[SEARCH_KEY] ? filter[SEARCH_KEY].value : '';
    const searchFormQuery = filter[SEARCH_FORM_KEY] ? filter[SEARCH_FORM_KEY].value : '';
    const filterContext = {
      registerSearchFilter: this.registerSearchFilter,
      registerSearchFormFilter: this.registerSearchFormFilter,
      onUpdateSearch: this.handleUpdateSearch,
      onUpdateSearchForm: this.handleUpdateSearchForm,
      searchQuery,
      searchFormQuery
    };
    const filters = additionalFilters?.sort((a, b) => {
      const orderA = a.order || 0;
      const orderB = b.order || 0;
      return orderA - orderB;
    }).map(filter => {
      const column = columns.find(col => col.dataKey === filter.dataKey);
      const label = filter.label || (column ? column.header : filter.dataKey);
      return {
        label,
        options: this.buildFilterOptions(filter),
        ...filter
      };
    });
    const activeFilters = Object.keys(filter).map(key => {
      let label = unwrapRef(filter[key].label);
      const checkboxLabels = filters?.find(f => f.dataKey === key && f.checkboxLabels)?.checkboxLabels;
      return {
        ...filter[key],
        label,
        checkboxLabels
      };
    }).filter(filter => unwrapRef(filter.isActive)(filter.value));
    return /*#__PURE__*/React.createElement(FilterContext.Provider, {
      value: filterContext
    }, /*#__PURE__*/React.createElement("div", {
      className: className
    }, /*#__PURE__*/React.createElement("div", {
      className: "asc-grid-filters"
    }, search, searchForm, filters && filters.length && /*#__PURE__*/React.createElement(Dropdown, {
      id: "asc-grid-filter-list-dropdown",
      buttonText: "FILTER LIST",
      closeWhen: closeFilters,
      content: /*#__PURE__*/React.createElement("div", {
        className: "asc-grid-filter-list"
      }, filters.map(f => /*#__PURE__*/React.createElement(DataGridFilter, {
        key: f.dataKey,
        activeValues: filter[f.dataKey] ? filter[f.dataKey].value : undefined,
        filter: f,
        onFilterChange: this.handleUpdateFilter,
        onRegisterFilter: this.registerAdditionalFilter
      })), /*#__PURE__*/React.createElement(ButtonGroup, {
        alignment: "center"
      }, /*#__PURE__*/React.createElement(Button, {
        className: "asc-btn--link asc-grid-filter-close",
        text: "CLOSE",
        clickHandler: () => this.closeFilters()
      })))
    })), activeFilters.length > 0 && /*#__PURE__*/React.createElement(DataGridFilterTags, {
      activeFilters: activeFilters,
      onClearFilter: this.handleClearFilter,
      onClearAllFilters: this.handleClearAllFilters
    })));
  }
}
DataGridFilters.propTypes = propTypes;
DataGridFilters.contextType = DataGridContext;